import React, {Component, ReactNode} from 'react';
import I18n from 'app/i18n';
import {inject, observer} from 'mobx-react';
import {isDesktop, isGuest, scrollToTop, scrollToTopSmooth} from 'app/utils';
import {lootboxUse, type LootboxOpenParams, type LootboxOpenResponse} from 'app/utils/lootbox_open/lootbox_use';
import NotificationStore from 'app/store/notifications';
import {DELAY_BEFORE_OPENING_LOOTBOX} from 'app/store/ArtifactStore';
import {toastError} from 'app/components/toasts/liteToast';
import SmartFullPageLoader from 'app/components/reusable/smart_loaders/SmartFullPageLoader';
import withRouter from 'app/utils/navigate/customWithRouter';
import GoBackButton from 'app/components/reusable/GoBackButton';
import {updateInfo} from 'app/common';
import {coolOffLootbox} from 'app/utils/isCoolOffActiveFor';
import {casinoGamesStore, userBonusesStore} from 'app/store/global';
import {ELEMENT_NOT_FOUND} from 'app/utils/Consts';
import {goToCasino} from 'app/utils/app_links';
import {translatedName} from 'app/utils/translations_helper';
import ShopItemDetailsHeader from 'app/components/shop/ShopItemDetailsHeader';
import ShopItemMeta from 'app/components/shop/lootboxes/ShopItemMeta';
import LootboxDetailsActions from './LootboxDetailsActions';
import LootboxDetailsRarityBar from './LootboxDetailsRarityBar';
import LootboxDetailsPrizes from './LootboxDetailsPrizes';
import LootboxDetailsCarousel, {LOOTBOX_CAROUSEL_OPENING_MS, lootboxCarouselResultDelayMs} from './LootboxDetailsCarousel';
import LootboxDetailsWin from './LootboxDetailsWin';
import InventoryItemInterface from 'app/interfaces/InventoryItemInterface';
import LootInterface from 'app/interfaces/LootInterface';
import ShopLootboxInterface from 'app/interfaces/shop/ShopLootboxInterface';
import {ShopLootboxStore} from 'app/store/ShopLootboxStore';
import {UserStore} from 'app/store/UserStore';
import UserInterface from 'app/interfaces/UserInterface';

interface Props {
    skipScrollToTop?: boolean
    isCasinoPage?: boolean
    params?: {name: string}
    shopLootboxStore?: ShopLootboxStore
    userStore?: UserStore
}

interface PendingPrize {
    artifact: InventoryItemInterface
    response: LootboxOpenResponse
}

interface State {
    hideQuickOpen: boolean
    idTimerShowPrize: ReturnType<typeof setTimeout> | null
    loading: boolean
    lootboxOpeningProcess: boolean
    openingInProgress: boolean
    spinPrize: LootInterface | null
    wonPrize: PendingPrize | null
}

class LootboxDetails extends Component<Props, State> {
    openingTimer: ReturnType<typeof setTimeout> | null = null;

    openDelayTimer: ReturnType<typeof setTimeout> | null = null;

    detailRef = React.createRef<HTMLDivElement>();

    quickOpen = false;

    prizeShown = false;

    openingSlug: string | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            hideQuickOpen: false,
            idTimerShowPrize: null,
            loading: true,
            lootboxOpeningProcess: false,
            openingInProgress: false,
            spinPrize: null,
            wonPrize: null
        };
        this.loadShopLootbox = this.loadShopLootbox.bind(this);
    }

    componentDidMount(): void {
        this.loadShopLootbox();

        if (!this.props.skipScrollToTop) {
            scrollToTop();
        }

        this.tryOpenNow();
    }

    componentDidUpdate(prevProps: Props): void {
        if (this.lootboxSlug !== prevProps.params?.name) {
            this.resetOpeningUi({loading: true, notifyLeave: true});
            this.loadShopLootbox();
        }
        this.tryOpenNow(true);
    }

    componentWillUnmount(): void {
        const {lootboxOpeningProcess} = this.state;
        const {shopLootboxStore} = this.props;

        shopLootboxStore?.reset();
        this.clearOpeningTimers();

        if (lootboxOpeningProcess) {
            NotificationStore.add({type: 'leave_lootbox_page'});
        }
    }

    tryOpenNow = (reload = false): void => {
        const {shopLootboxStore} = this.props;

        if (!shopLootboxStore?.openNowLootbox) {
            return;
        }

        this.setState({hideQuickOpen: true});

        if (reload) {
            this.loadShopLootbox();
        }

        this.openDelayTimer = setTimeout(this.open, DELAY_BEFORE_OPENING_LOOTBOX);
        shopLootboxStore.updateOpenNowStatus(false);
        shopLootboxStore.updateBuyAndOpenNowStatus(false);
    };

    get currency(): string {
        return new URLSearchParams(location.search).get('currency') || '';
    }

    get lootboxSlug(): string | undefined {
        return this.props.params?.name;
    }

    get shopLootbox(): ShopLootboxInterface | null {
        return this.props.shopLootboxStore?.lootbox ?? null;
    }

    isCurrentLootbox = (slug: string | null | undefined): boolean => slug === this.lootboxSlug;

    async loadShopLootbox(): Promise<boolean> {
        const {shopLootboxStore} = this.props;
        const slug = this.lootboxSlug;

        await shopLootboxStore?.updateLootboxBySlug(slug, this.currency);

        if (!this.isCurrentLootbox(slug)) {
            return false;
        }

        this.setState({loading: false});
        return true;
    }

    afterBuy = (): void => {
        this.setState({
            hideQuickOpen: true,
            lootboxOpeningProcess: true
        });
        this.scrollToOpeningBlock();
        this.loadShopLootbox().then((loaded: boolean) => {
            if (!loaded) {
                return;
            }

            updateInfo();
            this.openLootbox(false, true);
        });
    };

    loaderStart = (quick: boolean): void => {
        const {shopLootboxStore} = this.props;

        this.quickOpen = quick;
        this.prizeShown = false;
        this.openingSlug = this.lootboxSlug || null;
        this.setState({
            lootboxOpeningProcess: !quick,
            openingInProgress: true,
            spinPrize: null,
            wonPrize: null
        });
        shopLootboxStore?.updateLootboxOpeningInProgress(true);

        if (quick) {
            return;
        }

        this.openingTimer = setTimeout(() => {
            this.openingTimer = null;
            shopLootboxStore?.updateLootboxOpeningInProgress(false);
            updateInfo();
        }, LOOTBOX_CAROUSEL_OPENING_MS);
    };

    open = (): void => this.openLootbox(false);

    openQuick = (): void => this.openLootbox(true);

    openLootbox = (quick: boolean, immediate = false): void => {
        const {lootbox_to_open, user_lootbox_count} = this.shopLootbox ?? {};
        const {openingInProgress} = this.state;

        if (openingInProgress || user_lootbox_count === 0) {
            return;
        }

        if (lootbox_to_open) {
            const startOpen = (): void => {
                this.loaderStart(quick);
                lootboxUse(lootbox_to_open, {
                    doneOpenHandler: this.doneOpenHandler,
                    quick,
                    show_loading: false,
                    timeStart: Date.now()
                });
            };

            if (quick || immediate) {
                startOpen();
                return;
            }

            this.setState({lootboxOpeningProcess: true});
            this.scrollToOpeningBlock();
            this.openDelayTimer = setTimeout(startOpen, DELAY_BEFORE_OPENING_LOOTBOX);
            return;
        }
        this.setState({hideQuickOpen: false, lootboxOpeningProcess: false});
        toastError(I18n.t('server_error_try_later'));
    };

    showPrizeResult = (prize: PendingPrize): void => {
        if (this.prizeShown) {
            return;
        }

        this.prizeShown = true;
        this.setState({
            lootboxOpeningProcess: false,
            openingInProgress: false,
            wonPrize: prize
        });
        this.props.shopLootboxStore?.updateLootboxOpeningInProgress(false);
        this.loadShopLootbox();
        userBonusesStore.showNotViewedBonusPopup(true);
    };

    doneOpenHandler = (
        artifact: InventoryItemInterface,
        response: LootboxOpenResponse,
        params: LootboxOpenParams
    ): void => {
        if (!this.isCurrentLootbox(this.openingSlug)) {
            return;
        }

        const prize = {artifact, response};

        this.setState({spinPrize: response.extra_data?.[0] || null});

        if (params.quick) {
            this.props.shopLootboxStore?.updateLootboxOpeningInProgress(false);
            updateInfo();
            this.showPrizeResult(prize);
            return;
        }

        const delay = lootboxCarouselResultDelayMs(params.timeStart);

        this.setState({
            idTimerShowPrize: setTimeout(() => this.showPrizeResult(prize), delay)
        });
    };

    handleGoBackFromCasino = (): void => {
        this.props.shopLootboxStore?.setReturnToCasinoAllGamesOnLootboxBack(false);
        casinoGamesStore.changeFilterGroup(ELEMENT_NOT_FOUND);
        goToCasino();
    };

    clearOpeningTimers = (): void => {
        const {idTimerShowPrize} = this.state;

        if (idTimerShowPrize) {
            clearTimeout(idTimerShowPrize);
        }

        if (this.openingTimer) {
            clearTimeout(this.openingTimer);
            this.openingTimer = null;
        }

        if (this.openDelayTimer) {
            clearTimeout(this.openDelayTimer);
            this.openDelayTimer = null;
        }
    };

    resetOpeningUi = (options: {loading?: boolean, notifyLeave?: boolean} = {}): void => {
        const {lootboxOpeningProcess} = this.state;
        const {shopLootboxStore} = this.props;

        this.prizeShown = false;
        this.quickOpen = false;
        this.openingSlug = null;
        this.clearOpeningTimers();

        if (options.notifyLeave && lootboxOpeningProcess) {
            NotificationStore.add({type: 'leave_lootbox_page'});
        }

        this.setState({
            hideQuickOpen: false,
            idTimerShowPrize: null,
            loading: options.loading ? true : this.state.loading,
            lootboxOpeningProcess: false,
            openingInProgress: false,
            spinPrize: null,
            wonPrize: null
        });
        shopLootboxStore?.updateLootboxOpeningInProgress(false);
        shopLootboxStore?.updateAnimationFromOtherPageStatus(false);
    };

    handleBackToLootbox = (): void => {
        this.resetOpeningUi();
    };

    backClickHandler = (): (() => void) | null => {
        if (this.state.wonPrize) {
            return this.handleBackToLootbox;
        }

        if (this.props.isCasinoPage && this.props.shopLootboxStore?.returnToCasinoAllGamesOnLootboxBack) {
            return this.handleGoBackFromCasino;
        }

        return null;
    };

    scrollToOpeningBlock = (): void => {
        if (isDesktop()) {
            return;
        }

        if (this.props.isCasinoPage) {
            this.detailRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
            return;
        }

        scrollToTopSmooth();
    };

    openingAnimation = (): boolean => this.state.lootboxOpeningProcess ||
        this.props.shopLootboxStore?.animationLootboxFromOtherPage ||
        false;

    renderHeader = (lootbox: ShopLootboxInterface, hideShopLayout: boolean): ReactNode => {
        const {shopLootboxStore, userStore} = this.props;
        const {hideQuickOpen, openingInProgress} = this.state;
        const {recipe} = lootbox;
        const user = userStore?.user || {} as UserInterface;

        return <ShopItemDetailsHeader
            artifact={recipe}
            title={translatedName(recipe)}
            currency={this.currency}
            cost_money={lootbox.cost_money}
            cost_points={lootbox.cost_points}
            opening={hideShopLayout}
            meta={<ShopItemMeta
                loot={recipe?.loot}
                maxCalculatedPrice={recipe?.max_calculated_price}
            />}
            actions={<LootboxDetailsActions
                lootbox={lootbox}
                currency={this.currency}
                owned={!isGuest() && Number(lootbox.user_lootbox_count) > 0}
                openingInProgress={openingInProgress}
                disabled={coolOffLootbox(user)}
                buyNow={shopLootboxStore?.buyAndOpenNowLootbox || false}
                afterBuy={this.afterBuy}
                hideQuickOpen={hideQuickOpen || Boolean(shopLootboxStore?.openNowLootbox)}
                onOpen={this.open}
                onQuickOpen={this.openQuick}
            />}
        />;
    };

    renderOpeningStage = (lootbox: ShopLootboxInterface, prizes: LootInterface[], opening: boolean): ReactNode => {
        const showResult = Boolean(this.state.wonPrize);
        const {wonPrize} = this.state;
        const wonLoot = wonPrize?.response.extra_data?.[0];
        const winArtifact = wonPrize?.response.win_artifact;
        const user = this.props.userStore?.user || {} as UserInterface;
        const canSellArtifact = Boolean(
            winArtifact?.salable &&
            !user.restrict_artifact &&
            (wonLoot?.steam_skin || wonLoot?.recipe)
        );

        return <div className="lootbox-detail__opening-stage">
            {!showResult && <LootboxDetailsPrizes loot={prizes}/>}
            {!this.quickOpen && (opening || showResult) && <LootboxDetailsCarousel
                fading={showResult}
                loot={prizes}
                winner={this.state.spinPrize}
            />}
            {showResult && wonPrize && wonLoot && <LootboxDetailsWin
                lootboxId={wonPrize.artifact.id}
                prize={wonLoot}
                loot={prizes}
                winArtifact={winArtifact}
                steamSkin={wonPrize.response.steam_skin}
                steamTrade={wonPrize.response.steam_trade}
                canSellArtifact={canSellArtifact}
            />}
        </div>;
    };

    render(): ReactNode {
        const {loading, wonPrize} = this.state;
        const lootbox = this.shopLootbox;
        const opening = this.openingAnimation();

        if (loading) {
            return <SmartFullPageLoader/>;
        }

        if (!lootbox) {
            return null;
        }

        const prizes = lootbox.recipe?.loot ?? [];
        const showResult = Boolean(wonPrize);
        const hideShopLayout = opening || showResult;
        const backClick = this.backClickHandler();

        const detailClassName = [
            'market-details shop-item-detail lootbox-detail',
            hideShopLayout ? 'lootbox-detail--opening' : '',
            hideShopLayout && this.quickOpen ? 'lootbox-detail--quick-open' : ''
        ].filter(Boolean).join(' ');

        return <div className={detailClassName} ref={this.detailRef}>
            <GoBackButton
                label={I18n.t(showResult ? 'shop.back_to_lootbox' : 'shop.back_to_store')}
                {...(backClick ? {onClick: backClick} : {})}
            />
            {this.renderHeader(lootbox, hideShopLayout)}
            <div className="shop-item-detail__content">
                <div className="lootbox-detail__overview">
                    <h3 className="shop-item-detail__section-title">{I18n.t('shop.lootbox_prizes_title')}</h3>
                    <LootboxDetailsRarityBar loot={prizes}/>
                </div>
                {this.renderOpeningStage(lootbox, prizes, opening)}
            </div>
        </div>;
    }
}

export default withRouter(inject('shopLootboxStore', 'userStore')(observer(LootboxDetails)));
